import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ChannelLogo } from './channel-logo';
import { SearchIcon, XIcon } from './icons';
import { useFeedSearch } from '../hooks/use-feed-query';
import { useChannelSearchQuery } from '../hooks/use-channels-query';

// Top-nav search box with an inline dropdown that shows the top 5 articles
// and top 5 channels matching the query. Hits Enter → navigate to a full
// search results page.
export function SearchBar() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const articleResults = useFeedSearch(query);
  const channelResults = useChannelSearchQuery(query);

  // Close on outside click.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  const trimmed = query.trim();
  const showDropdown = open && trimmed.length >= 2;
  const articles = articleResults.data ?? [];
  const channels = channelResults.data ?? [];
  const loading = articleResults.isLoading || channelResults.isLoading;
  const empty = !loading && articles.length === 0 && channels.length === 0;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (trimmed.length < 2) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  return (
    <div className="nw-search" ref={wrapperRef}>
      <form className="nw-search__form" onSubmit={submit} role="search">
        <span className="nw-search__icon" aria-hidden>
          <SearchIcon size={15} stroke={2} />
        </span>
        <input
          type="search"
          className="nw-search__input"
          placeholder={t('web.searchBar.placeholder')}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          aria-label={t('web.searchBar.ariaLabel')}
        />
        {query && (
          <button
            type="button"
            className="nw-search__clear"
            onClick={() => {
              setQuery('');
              setOpen(false);
            }}
            aria-label={t('web.searchBar.clearAriaLabel')}
          >
            <XIcon size={12} stroke={2.4} />
          </button>
        )}
      </form>
      {showDropdown && (
        <div className="nw-search__dropdown" role="listbox">
          {loading && (
            <div className="nw-search__hint">
              <span className="nw-feed__loading-spinner" aria-hidden />
              {t('web.searchBar.loading')}
            </div>
          )}
          {!loading && empty && <div className="nw-search__hint">{t('web.searchBar.empty', { query: trimmed })}</div>}
          {channels.length > 0 && (
            <>
              <div className="nw-search__group">{t('web.searchBar.groupChannels')}</div>
              {channels.slice(0, 5).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="nw-search__row"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/channel/${encodeURIComponent(c.id)}`);
                  }}
                >
                  <ChannelLogo name={c.name} logoUrl={c.logoUrl} size={28} />
                  <div className="nw-search__row-text">
                    <div className="nw-search__row-title">{c.name}</div>
                    <div className="nw-search__row-meta">
                      {c.language?.toUpperCase()}
                      {c.country ? ` · ${c.country}` : ''}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
          {articles.length > 0 && (
            <>
              <div className="nw-search__group">{t('web.searchBar.groupArticles')}</div>
              {articles.slice(0, 5).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="nw-search__row"
                  onClick={() => {
                    setOpen(false);
                    navigate(`/article/${encodeURIComponent(a.id)}`);
                  }}
                >
                  <ChannelLogo name={a.channelName} logoUrl={a.channelLogo} size={28} />
                  <div className="nw-search__row-text">
                    <div className="nw-search__row-title">{a.title}</div>
                    <div className="nw-search__row-meta">{a.channelName}</div>
                  </div>
                </button>
              ))}
            </>
          )}
          {trimmed.length >= 2 && (
            <button
              type="button"
              className="nw-search__see-all"
              onClick={() => {
                setOpen(false);
                navigate(`/search?q=${encodeURIComponent(trimmed)}`);
              }}
            >
              {t('web.searchBar.seeAllResults', { query: trimmed })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
